/**
 * Capture the current screen as a JPEG blob.
 *
 * Constraints that shape this:
 *
 * 1. html2canvas cannot render `backdrop-filter`, and the app is
 *    glassmorphism-heavy. During capture the root carries a
 *    `review-capturing` class whose CSS (src/index.css) swaps blur for solid
 *    backgrounds — without it every glass card in the capture looks broken.
 *
 * 2. Captures of a dense page take 1–3s and run on the main thread — the
 *    caller shows a busy state, and a hard timeout guarantees the note can
 *    still be saved (image-less) if the capture hangs.
 *
 * 3. Redaction must be irreversible: `redact` shapes are burned into the
 *    JPEG's pixels here, before upload. The vector layer being removable must
 *    never reveal what was underneath.
 *
 * Anything marked `data-review-overlay` (toolbar, drawn shapes, composer) is
 * excluded, so the stored image is the clean screen; the OTHER shapes are
 * re-drawn over it as vectors at display time.
 */
import html2canvas from 'html2canvas';
import type { ReviewShape } from '../../types/screenReview.types';

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.7;
/** Hard cap — a hung capture must not hold the note hostage. */
const CAPTURE_TIMEOUT_MS = 8000;

export async function captureScreenshot(
  shapes: ReviewShape[] = [],
): Promise<Blob | null> {
  const root = document.getElementById('root') ?? document.body;
  document.documentElement.classList.add('review-capturing');
  try {
    const capture = html2canvas(root, {
      useCORS: true,
      logging: false,
      // scale 1: viewport pixels, not retina — halves memory and the result
      // is downscaled to ≤1600px anyway.
      scale: 1,
      // Crop to the VISIBLE viewport. Without this, html2canvas renders the
      // whole scrollable page: the image squashes in a viewport-shaped box
      // and the 0–1 shape coordinates no longer point at what was marked.
      // Cropping makes image aspect ratio ≡ coordinate space, so every
      // consumer (card, lightbox, PDF, email) lines up for free.
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      ignoreElements: (el) => el.hasAttribute?.('data-review-overlay') ?? false,
    });

    const canvas = await Promise.race([
      capture,
      new Promise<null>((resolve) =>
        window.setTimeout(() => resolve(null), CAPTURE_TIMEOUT_MS),
      ),
    ]);
    if (!canvas) return null;

    const scale = Math.min(1, MAX_WIDTH / canvas.width);
    let output = canvas;
    if (scale < 1) {
      const small = document.createElement('canvas');
      small.width = Math.round(canvas.width * scale);
      small.height = Math.round(canvas.height * scale);
      const ctx = small.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, 0, small.width, small.height);
        output = small;
      }
    }

    // Burn redactions into the pixels. Coordinates are viewport fractions, so
    // they map straight onto the (possibly downscaled) output canvas.
    const redactions = shapes.filter((s) => s.kind === 'redact');
    if (redactions.length > 0) {
      const ctx = output.getContext('2d');
      if (!ctx) return null; // never ship an un-redacted image
      ctx.fillStyle = '#111827';
      for (const r of redactions) {
        if (r.kind !== 'redact') continue;
        ctx.fillRect(
          Math.floor(r.x * output.width),
          Math.floor(r.y * output.height),
          Math.ceil(r.w * output.width),
          Math.ceil(r.h * output.height),
        );
      }
    }

    return await new Promise<Blob | null>((resolve) =>
      output.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY),
    );
  } catch {
    // A failed capture must never block saving the note itself — the vectors
    // and anchor still carry the feedback.
    return null;
  } finally {
    document.documentElement.classList.remove('review-capturing');
  }
}
